<template>
  <fieldset :aria-busy="submitting ? 'true' : 'false'">
    <form
      @submit.prevent="handleSubmit"
      class="needs-validation"
      novalidate
      :aria-describedby="formErrorId"
    >
      <div class="form-row">
        <div class="form-group col-sm-12 col-md-6">
          <label :for="idForField('firstname')">
            {{ __("e-mail.contact.form.label.firstname") }} *
          </label>
          <input
            type="text"
            :id="idForField('firstname')"
            name="given-name"
            class="form-control"
            v-model.trim="form.firstname"
            required
            aria-required="true"
            autocomplete="given-name"
            :placeholder="__('e-mail.contact.form.placeholder.firstname')"
            :aria-invalid="Boolean(errors.firstname)"
            aria-describedby="firstname-feedback"
            :class="inputStateClass('firstname')"
            @blur="touch('firstname')"
          />
          <div id="firstname-feedback" class="invalid-feedback">
            {{ errors.firstname }}
          </div>
        </div>

        <div class="form-group col-sm-12 col-md-6">
          <label :for="idForField('lastname')">
            {{ __("e-mail.contact.form.label.lastname") }} *
          </label>
          <input
            type="text"
            :id="idForField('lastname')"
            name="family-name"
            class="form-control"
            v-model.trim="form.lastname"
            required
            aria-required="true"
            autocomplete="family-name"
            :placeholder="__('e-mail.contact.form.placeholder.lastname')"
            :aria-invalid="Boolean(errors.lastname)"
            aria-describedby="lastname-feedback"
            :class="inputStateClass('lastname')"
            @blur="touch('lastname')"
          />
          <div id="lastname-feedback" class="invalid-feedback">
            {{ errors.lastname }}
          </div>
        </div>
      </div>

      <div class="form-group">
        <label :for="idForField('email')">
          {{ __("e-mail.contact.form.label.email") }} *
        </label>
        <input
          type="email"
          :id="idForField('email')"
          name="email"
          class="form-control"
          v-model.trim="form.email"
          inputmode="email"
          required
          aria-required="true"
          autocomplete="email"
          :placeholder="__('e-mail.contact.form.placeholder.email')"
          :aria-invalid="Boolean(errors.email)"
          aria-describedby="email-feedback"
          :class="inputStateClass('email')"
          @blur="touch('email')"
        />
        <div id="email-feedback" class="invalid-feedback">
          {{ errors.email }}
        </div>
      </div>

      <div class="form-row">
        <div class="form-group col-sm-12 col-md-6">
          <label :for="idForField('business')">
            {{ __("e-mail.contact.form.label.business") }}
          </label>
          <input
            type="text"
            :id="idForField('business')"
            name="organization"
            class="form-control"
            v-model.trim="form.business"
            autocomplete="organization"
            :placeholder="__('e-mail.contact.form.placeholder.business')"
            :aria-invalid="Boolean(errors.business)"
            aria-describedby="business-feedback"
            :class="inputStateClass('business')"
            @blur="touch('business')"
          />
          <div id="business-feedback" class="invalid-feedback">
            {{ errors.business }}
          </div>
        </div>

        <div class="form-group col-sm-12 col-md-6">
          <label :for="idForField('phone')">
            {{ __("e-mail.contact.form.label.phone") }}
          </label>
          <input
            type="tel"
            :id="idForField('phone')"
            name="tel"
            class="form-control"
            v-model.trim="form.phone"
            inputmode="tel"
            autocomplete="tel"
            :placeholder="__('e-mail.contact.form.placeholder.phone')"
            :aria-invalid="Boolean(errors.phone)"
            aria-describedby="phone-feedback"
            :class="inputStateClass('phone')"
            @blur="touch('phone')"
          />
          <div id="phone-feedback" class="invalid-feedback">
            {{ errors.phone }}
          </div>
        </div>
      </div>

      <div class="form-group">
        <label :for="idForField('message')">
          {{ __("e-mail.contact.form.label.message") }} *
        </label>
        <textarea
          :id="idForField('message')"
          name="message"
          class="form-control"
          v-model.trim="form.message"
          rows="5"
          required
          aria-required="true"
          :placeholder="__('e-mail.contact.form.placeholder.message')"
          :aria-invalid="Boolean(errors.message)"
          aria-describedby="message-feedback"
          :class="inputStateClass('message')"
          @blur="touch('message')"
        ></textarea>
        <div id="message-feedback" class="invalid-feedback">
          {{ errors.message }}
        </div>
      </div>

      <small class="text-muted d-block mb-3 float-right"
        >*
        {{ __("e-mail.contact.form.required_hint") || "Pflichtfelder" }}</small
      >

      <button
        type="submit"
        class="btn btn-lg btn-primary w-100 w-md-auto text-uppercase"
        :disabled="submitting || !canSubmit"
      >
        <span v-if="submitting">
          <span
            class="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          ></span>
          {{ __("e-mail.contact.form.pending") }}
        </span>
        <span v-else>{{ __("e-mail.contact.form.submit_btn") }}</span>
      </button>
    </form>

    <div
      v-if="successMessage"
      class="alert alert-success mt-4"
      role="status"
      aria-live="polite"
    >
      {{ successMessage }}
    </div>

    <div
      v-if="errorMessage"
      class="alert alert-danger mt-4"
      :id="formErrorId"
      role="alert"
      aria-live="assertive"
    >
      {{ errorMessage }}
    </div>
  </fieldset>
</template>

<script>
import trans from "../mixins/trans";

export default {
  name: "ContactForm",
  mixins: [trans],

  props: {
    formTitle: { type: String, default: "Anfrage" },
    endpoint: { type: String, default: "/contact" },
  },

  data() {
    return {
      form: this.getEmptyForm(),
      touched: {},
      errors: {},
      submitting: false,
      successMessage: "",
      errorMessage: "",
    };
  },

  computed: {
    idForField() {
      const uid = this._uid;
      return (field) => `${field}-${uid}`;
    },
    formErrorId() {
      return `form-error-${this._uid}`;
    },
    canSubmit() {
      return Boolean(
        this.form.firstname &&
          this.form.lastname &&
          this.form.email &&
          this.form.message &&
          Object.keys(this.errors).length === 0
      );
    },
  },

  methods: {
    getEmptyForm() {
      return {
        firstname: "",
        lastname: "",
        email: "",
        business: "",
        phone: "",
        message: "",
      };
    },

    inputStateClass(field) {
      if (!this.touched[field]) return {};
      return {
        "is-valid": this.form[field] && !this.errors[field],
        "is-invalid": Boolean(this.errors[field]),
      };
    },

    touch(field) {
      this.$set(this.touched, field, true);
      const { [field]: _, ...rest } = this.errors;
      const single = this.validateSingle(field);
      this.errors = { ...rest, ...(single ? { [field]: single } : {}) };
    },

    validateSingle(field) {
      const t = this.__;
      const v = (s) => (typeof s === "string" ? s.trim() : s);

      switch (field) {
        case "firstname":
          if (!v(this.form.firstname))
            return t("e-mail.contact.form.validate.errors.firstname");
          break;
        case "lastname":
          if (!v(this.form.lastname))
            return t("e-mail.contact.form.validate.errors.lastname");
          break;
        case "email":
          if (!v(this.form.email))
            return t("e-mail.contact.form.validate.errors.email");
          if (!this.validEmail(this.form.email))
            return t("e-mail.contact.form.validate.errors.valid_email");
          break;
        case "message":
          if (!v(this.form.message))
            return t("e-mail.contact.form.validate.errors.message");
          break;
        case "phone":
          if (
            v(this.form.phone) &&
            !/^\+?[0-9 ()\-]{6,}$/.test(this.form.phone)
          ) {
            return t("e-mail.contact.form.validate.errors.valid_phone");
          }
          break;
      }
      return "";
    },

    validateForm() {
      const fields = [
        "firstname",
        "lastname",
        "email",
        "message",
        "business",
        "phone",
      ];
      const errors = {};
      fields.forEach((f) => {
        const e = this.validateSingle(f);
        if (e) errors[f] = e;
      });
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },

    sanitizePayload(data) {
      const trim = (s) => (typeof s === "string" ? s.trim() : s);
      return {
        formTitle: this.formTitle,
        firstname: trim(data.firstname),
        lastname: trim(data.lastname),
        email: trim(data.email),
        business: trim(data.business),
        phone: trim(data.phone),
        message: trim(data.message),
      };
    },

    async handleSubmit() {
      this.submitting = true;
      this.successMessage = "";
      this.errorMessage = "";
      const ok = this.validateForm();
      Object.keys(this.form).forEach((k) => this.$set(this.touched, k, true));

      if (!ok) {
        this.submitting = false;
        return;
      }

      try {
        const payload = this.sanitizePayload(this.form);
        const res = await axios.post(this.endpoint, payload);

        this.successMessage =
          res?.data?.message || this.__("e-mail.contact.form.success");
        this.form = this.getEmptyForm();
        this.errors = {};
        this.touched = {};
      } catch (err) {
        const serverErrors = err?.response?.data?.errors;
        if (serverErrors && typeof serverErrors === "object") {
          this.errors = { ...this.errors, ...serverErrors };
        }
        this.errorMessage =
          err?.response?.data?.message || this.__("e-mail.contact.form.error");
      } finally {
        this.submitting = false;
      }
    },

    validEmail(email) {
      const re = /^[a-zA-Z0-9äöüÄÖÜß._%+-]+@[a-zA-Z0-9äöüÄÖÜß.-]+\.[a-zA-Z]{2,}$/;
      return re.test(email);
    },
  },
};
</script>
